'use client';

/**
 * Thinking-timeline step model + presentational rows.
 *
 * `buildThinkingModel` turns a turn's commentary lines + RAG references into
 * an ordered set of steps (globe → media → sparkle) plus the collapsed
 * summary + favicon strip. The steps are ~90% fixed template — only the
 * topic, the media-kind phrase, and the commentary are live.
 */

import React from 'react';
import { FileText } from 'lucide-react';
import { MessageResponse } from '@/components/ai-elements/message';
import type { RAGReference } from '@/types/rag';
import { cn } from '@/lib/utils';
import { getYouTubeId, youTubeThumbnailUrl } from '@/lib/youtube';
import {
  deriveMediaKind,
  deriveTopic,
  mediaKindGlyph,
  MEDIA_KIND_PHRASE,
  MEDIA_KIND_SUMMARY,
  type MediaKind,
  type StepGlyph,
} from './media-kind';

// ── Models ─────────────────────────────────────────────────────────

export interface SourceChipModel {
  hostname: string;
  favicon: string | null;
  isYouTube: boolean;
}

export interface VideoChipModel {
  url: string;
  youtubeId: string;
  thumbnail: string;
  title: string;
  sourceLabel: string;
}

export interface ArticleChipModel {
  url: string;
  domain: string;
  favicon: string | null;
  title: string;
  sourceLabel: string;
}

export type ThinkingStep =
  | {
      kind: 'globe';
      id: string;
      topic: string | null;
      chips: SourceChipModel[];
    }
  | {
      kind: 'media';
      id: string;
      glyph: StepGlyph;
      phrase: string;
      video: VideoChipModel | null;
      article: ArticleChipModel | null;
    }
  | { kind: 'sparkle'; id: string; text: string };

export interface ThinkingModel {
  steps: ThinkingStep[];
  faviconStrip: SourceChipModel[];
  summary: string;
}

function hostOf(url: string): { hostname: string; favicon: string } | null {
  try {
    const h = new URL(url).hostname;
    return {
      hostname: h.replace(/^www\./, ''),
      favicon: `https://www.google.com/s2/favicons?domain=${h}&sz=32`,
    };
  } catch {
    return null;
  }
}

function isYouTubeHost(hostname: string): boolean {
  return (
    hostname === 'youtube.com' ||
    hostname === 'youtu.be' ||
    hostname.endsWith('.youtube.com')
  );
}

export function buildThinkingModel(
  commentary: string[],
  refs: RAGReference[]
): ThinkingModel {
  const sorted = [...refs].sort((a, b) => (b.score ?? 0) - (a.score ?? 0));

  const chipMap = new Map<string, SourceChipModel>();
  for (const r of sorted) {
    const h = hostOf(r.url);
    if (!h || chipMap.has(h.hostname)) continue;
    chipMap.set(h.hostname, {
      hostname: h.hostname,
      favicon: h.favicon,
      isYouTube: isYouTubeHost(h.hostname),
    });
  }
  const chips = [...chipMap.values()];

  const steps: ThinkingStep[] = [];

  // Chronological order: Sara's commentary (her opening words) comes first,
  // then the sources she consulted, then the media she pulled.
  commentary.forEach((text, i) => {
    const t = text.trim();
    if (t) steps.push({ kind: 'sparkle', id: `sparkle-${i}`, text: t });
  });

  if (sorted.length > 0) {
    steps.push({
      kind: 'globe',
      id: 'globe',
      topic: deriveTopic(sorted),
      chips,
    });
  }

  // Featured media = top-scored YouTube ref, else the top-scored ref overall.
  const featured =
    sorted.find((r) => getYouTubeId(r.url) !== null) ?? sorted[0];
  let mediaKind: MediaKind | null = null;
  if (featured) {
    mediaKind = deriveMediaKind(featured);
    const yid = getYouTubeId(featured.url);
    const h = hostOf(featured.url);
    if (yid) {
      steps.push({
        kind: 'media',
        id: 'media',
        glyph: mediaKindGlyph(mediaKind),
        phrase: MEDIA_KIND_PHRASE[mediaKind],
        video: {
          url: featured.url,
          youtubeId: yid,
          thumbnail: youTubeThumbnailUrl(yid),
          title: featured.title,
          sourceLabel: `YouTube · ${featured.category}`,
        },
        article: null,
      });
    } else {
      steps.push({
        kind: 'media',
        id: 'media',
        glyph: mediaKindGlyph(mediaKind),
        phrase: MEDIA_KIND_PHRASE[mediaKind],
        video: null,
        article: {
          url: featured.url,
          domain: h?.hostname ?? featured.url,
          favicon: h?.favicon ?? null,
          title: featured.title,
          sourceLabel: h
            ? `${h.hostname} · ${featured.category}`
            : featured.category,
        },
      });
    }
  }

  const summary = mediaKind
    ? `Consulted sources · ${MEDIA_KIND_SUMMARY[mediaKind]}`
    : sorted.length > 0
      ? 'Consulted Art of Living guidance'
      : 'Sharing one steady thing to lean on';

  return { steps, faviconStrip: chips, summary };
}

// ── Icons ──────────────────────────────────────────────────────────
//
// [Turn 18] Steps live INSIDE the tinted evidence block now — no circles,
// no gutter rail (that was the 4a anatomy, superseded). Each step indents
// its text 26px and hangs a bare warm-neutral #a89a82 glyph in a 16×18
// slot at the left edge: text lines for reasoning (Sara's own thought),
// globe for consulted sources, an OUTLINE play triangle for pulled media
// (the mock strokes it, never fills).

type GlyphName = 'lines' | 'globe' | StepGlyph;

function Glyph({ name }: { name: GlyphName }): React.ReactElement {
  switch (name) {
    case 'lines':
      return (
        <svg
          width={15}
          height={15}
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth={2}
          strokeLinecap="round"
        >
          <path d="M5 7h14M5 12h10M5 17h6" />
        </svg>
      );
    case 'globe':
      return (
        <svg
          width={15}
          height={15}
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth={1.8}
        >
          <circle cx="12" cy="12" r="9" />
          <path d="M3 12h18" />
          <path d="M12 3c2.4 2.4 3.8 5.6 3.8 9s-1.4 6.6-3.8 9-3.8-5.6-3.8-9S9.6 5.4 12 3z" />
        </svg>
      );
    case 'youtube':
      return (
        <svg
          width={13}
          height={13}
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth={1.8}
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <path d="M7 5l12 7-12 7z" />
        </svg>
      );
    case 'sparkle':
      return (
        <svg width={12} height={12} viewBox="0 0 24 24" fill="currentColor">
          <path d="M12 2l1.9 5.6L19.5 9l-4.5 3.9L16.2 19 12 15.8 7.8 19l1.2-6.1L4.5 9l5.6-1.4z" />
        </svg>
      );
    case 'article':
      return (
        <svg
          width={13}
          height={13}
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth={1.8}
          strokeLinecap="round"
        >
          <path d="M4 5h16M4 12h16M4 19h10" />
        </svg>
      );
  }
}

function StepIcon({ name }: { name: GlyphName }): React.ReactElement {
  return (
    <span
      className={cn(
        'absolute left-0 flex h-[18px] w-[16px] items-center justify-center text-[#a89a82]',
        // The play triangle sits a hair lower in the mock (top:1px).
        name === 'youtube' ? 'top-[1px]' : 'top-0'
      )}
    >
      <Glyph name={name} />
    </span>
  );
}

/** [Turn 18] Every step's text shares one voice: Lora italic, warm grey —
 *  the whole trace reads as Sara's inner monologue. */
const STEP_TEXT =
  'font-[family-name:var(--font-lora)] text-[13px] italic leading-[1.55] text-[#7a7062]';

/** Streamdown renders FULL markdown, but a timeline step is 13px inner
 *  monologue — every block element it can emit must collapse to the step's
 *  own scale, or a heading/list in commentary renders answer-sized inside
 *  the trace (plus Bootstrap's global <p>/<ul> margins gap it open).
 *  Individual selectors on purpose: comma groups inside one arbitrary
 *  variant (`[&_:is(h1,h2)]`) are a Tailwind compile risk. */
const TIMELINE_MARKDOWN_RESET = cn(
  '[&_p]:my-0',
  '[&_h1]:text-[13px] [&_h2]:text-[13px] [&_h3]:text-[13px]',
  '[&_h4]:text-[13px] [&_h5]:text-[13px] [&_h6]:text-[13px]',
  '[&_h1]:font-semibold [&_h2]:font-semibold [&_h3]:font-semibold',
  '[&_h4]:font-semibold [&_h5]:font-semibold [&_h6]:font-semibold',
  '[&_h1]:my-0 [&_h2]:my-0 [&_h3]:my-0',
  '[&_h4]:my-0 [&_h5]:my-0 [&_h6]:my-0',
  '[&_ul]:my-0 [&_ol]:my-0 [&_li]:my-0 [&_blockquote]:my-0',
  '[&_ul]:pl-[18px] [&_ol]:pl-[18px]'
);

// ── Chips ──────────────────────────────────────────────────────────

function FaviconImg({
  src,
  size,
  className,
}: {
  src: string;
  size: number;
  className?: string;
}): React.ReactElement {
  return (
    // eslint-disable-next-line @next/next/no-img-element
    <img
      src={src}
      alt=""
      width={size}
      height={size}
      style={{ width: size, height: size }}
      className={cn('flex-none rounded-sm object-contain', className)}
      onError={(e) => {
        e.currentTarget.style.display = 'none';
      }}
    />
  );
}

function YtDot({ size = 14 }: { size?: number }): React.ReactElement {
  return (
    <span
      aria-hidden
      style={{ width: size, height: size }}
      className="inline-flex flex-none items-center justify-center rounded-[3px] bg-[#ff2d2d]"
    >
      <span
        style={{
          width: 0,
          height: 0,
          borderTop: `${Math.round(size * 0.22)}px solid transparent`,
          borderBottom: `${Math.round(size * 0.22)}px solid transparent`,
          borderLeft: `${Math.round(size * 0.36)}px solid #fff`,
          marginLeft: 1,
        }}
      />
    </span>
  );
}

function SourceChipView({
  chip,
}: {
  chip: SourceChipModel;
}): React.ReactElement {
  return (
    <span className="inline-flex items-center gap-[6px] rounded-[18px] border-[1px] border-[#f0e3cf] bg-white py-[5px] pl-[7px] pr-[11px] text-[12.5px] text-[#5b6072]">
      {chip.isYouTube ? (
        <YtDot size={16} />
      ) : chip.favicon ? (
        <FaviconImg src={chip.favicon} size={16} className="rounded-[4px]" />
      ) : null}
      {chip.hostname}
    </span>
  );
}

const CHIP_SHELL =
  'mt-[9px] flex max-w-[360px] items-center gap-[11px] rounded-[11px] border-[1px] border-[#f0e3cf] bg-white px-[11px] py-[9px] no-underline transition-colors hover:border-[#e3d5c1]';

function VideoChipView({
  video,
}: {
  video: VideoChipModel;
}): React.ReactElement {
  return (
    <a
      href={video.url}
      target="_blank"
      rel="noopener noreferrer"
      title={video.title}
      className={CHIP_SHELL}
    >
      {/* [Turn 18] Bare 56×44 thumb — the mock's in-block card carries no
          play badge and no shadow (the evidence stays quiet; the hero video
          below the answer is where the big play affordance lives). */}
      <span className="relative block h-[44px] w-[56px] flex-none overflow-hidden rounded-[8px] bg-black">
        {/* eslint-disable-next-line @next/next/no-img-element */}
        <img src={video.thumbnail} alt="" className="size-full object-cover" />
      </span>
      <span className="min-w-0">
        <span className="line-clamp-2 text-[13px] font-semibold leading-[1.35] text-[#31364E]">
          {video.title}
        </span>
        <span className="mt-[2px] block text-[11.5px] text-[#9a8f7f]">
          {video.sourceLabel}
        </span>
      </span>
    </a>
  );
}

function ArticleChipView({
  article,
}: {
  article: ArticleChipModel;
}): React.ReactElement {
  return (
    <a
      href={article.url}
      target="_blank"
      rel="noopener noreferrer"
      title={article.title}
      className={CHIP_SHELL}
    >
      <span className="flex h-[44px] w-[56px] flex-none items-center justify-center overflow-hidden rounded-[8px] bg-gradient-to-br from-[#fff2e6] to-[#ffe0c2]">
        {article.favicon ? (
          <FaviconImg src={article.favicon} size={18} />
        ) : (
          <FileText className="size-4 text-[#e8600f]" strokeWidth={1.8} />
        )}
      </span>
      <span className="min-w-0">
        <span className="line-clamp-2 text-[13px] font-semibold leading-[1.35] text-[#31364E]">
          {article.title}
        </span>
        <span className="mt-[2px] block text-[11.5px] text-[#9a8f7f]">
          {article.sourceLabel}
        </span>
      </span>
    </a>
  );
}

// ── Step row ───────────────────────────────────────────────────────

export function StepRow({
  step,
  dimmed = false,
}: {
  step: ThinkingStep;
  /** [Turn 18] True on the newest step while the turn is still working —
   *  the mock renders the in-flight step at 62% opacity. */
  dimmed?: boolean;
}): React.ReactElement {
  if (step.kind === 'globe') {
    return (
      <div className={cn('relative pl-[26px]', dimmed && 'opacity-[0.62]')}>
        <StepIcon name="globe" />
        <div className={STEP_TEXT}>
          Checked trusted sources{step.topic ? <> on {step.topic}</> : null}
        </div>
        {step.chips.length > 0 && (
          <div className="mt-[9px] flex flex-wrap gap-[8px]">
            {step.chips.map((c) => (
              <SourceChipView key={c.hostname} chip={c} />
            ))}
          </div>
        )}
      </div>
    );
  }

  if (step.kind === 'media') {
    return (
      <div className={cn('relative pl-[26px]', dimmed && 'opacity-[0.62]')}>
        <StepIcon name={step.glyph} />
        <div className={STEP_TEXT}>{step.phrase}</div>
        {step.video && <VideoChipView video={step.video} />}
        {step.article && <ArticleChipView article={step.article} />}
      </div>
    );
  }

  // Reasoning/commentary — Sara's own thought, marked by the text-lines
  // glyph (Turn 18 dropped the mini orb along with the 4a gutter). Rendered
  // through MessageResponse (Streamdown, the repo's one markdown path) so a
  // message that streams here before its classification settles never shows
  // raw **markers**; STEP_TEXT wins over Streamdown's defaults via the
  // wrapper, and TIMELINE_MARKDOWN_RESET collapses every markdown block
  // element to the step's 13px scale.
  return (
    <div className={cn('relative pl-[26px]', dimmed && 'opacity-[0.62]')}>
      <StepIcon name="lines" />
      <div className={cn(STEP_TEXT, TIMELINE_MARKDOWN_RESET)}>
        <MessageResponse>{step.text}</MessageResponse>
      </div>
    </div>
  );
}
